import { Col, Collapse, Row, Space } from "antd";
import { CollapsePanelProps, CollapseProps } from "antd/lib/collapse";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { COREProgress } from "../Feedback/COREProgress";
import "./COREContainerPanel.less";
import { TestID, TestIDWrapper } from "../../shared/testids/testids";
import { COREHeading } from "../Typography/COREHeading";
import { CORESelect, CORESelectProps } from "../Form/CORESelect";
import { COREButton } from "../Action/COREButton";
import { COREMenuItem } from "../Navigation/COREMenuItem";
import classNames from "classnames";
import {
  defaultResizeValue,
  OnResize,
  SetSizeObject,
} from "../../shared/OnResize";
import { COREDropdownMenu, DropdownMenuItem } from "../Form/COREDropdownMenu";
import { CORECSVButton, CORECSVButtonProps } from "../Action/CORECSVButton";
import { COREIcon } from "../Content/COREIcon";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { spacing } from "../../shared/global";
import { ContentLockedTooltip } from "../../shared/tooltips/ContentLockedTooltip";
import { UpgradeType } from "../../shared/modals/SubscriptionUpgradeModal";

export type InteractionProps =
  | React.ReactElement<typeof COREButton>
  | React.ReactElement<typeof CORESelect>;

type LeftRightInteractionMenuProps = {
  menuType?: "useLeftRightForMenu";
  interactionsLeft?: InteractionProps[];
  interactionsRight?: InteractionProps[];
  interactionsMenu?: DropdownMenuItem[];
};

type DifferentInteractionMenuProps = {
  menuType: "differentMenu";
  interactionsLeft?: InteractionProps[];
  interactionsRight?: InteractionProps[];
  interactionsMenu: DropdownMenuItem[];
};

export type InteractionMenuItem = DropdownMenuItem;

type CommonProps = {
  isOpen?: boolean;
  bannerImageUrl?: string;
  bannerImageHeight?: number;
  maxHeight?: number;
  noScroll?: boolean;
  allowOverflow?: boolean;
  progress?: React.ReactElement<typeof COREProgress>;
  interactionSpace?: number;
  expandIconSize?: SetSizeObject;
  testID: TestID;
  onShowMenu?: (isShowMenu: boolean) => void;
  headerSummary?: React.ReactNode;
  expandIcon?: CollapseProps["expandIcon"];
  type?: "panel" | "container";
  roundCorner?: boolean;
  showExpandIconWhenDisabled?: boolean;
  fullWidthHeader?: boolean;
  /**
   * Drops the gap between the header and the content. Use when the header owns
   * its own bottom spacing — e.g. a chart panel whose header is a tab bar that
   * has to sit directly on the content divider.
   */
  flushHeader?: boolean;
} & (
  | {
      showContentLockedTooltip: boolean;
      upgradeType: UpgradeType;
    }
  | {
      showContentLockedTooltip?: undefined;
      upgradeType?: undefined;
    }
);

export type COREAccordionItemProps = CommonProps &
  (LeftRightInteractionMenuProps | DifferentInteractionMenuProps) &
  CollapsePanelProps;

type HeaderWithInteractionsProps = {
  stopPropagationWhenNotCollapsed: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => void;
  isActive: boolean;
  showMenu: boolean;
  isDisabled: boolean;
  interactionSpace: Exclude<
    COREAccordionItemProps["interactionSpace"],
    undefined
  >;
} & Pick<
  COREAccordionItemProps,
  | "headerSummary"
  | "collapsible"
  | "expandIcon"
  | "testID"
  | "header"
  | "interactionsLeft"
  | "showExpandIconWhenDisabled"
  | "fullWidthHeader"
>;

export const InteractionMenuButton: React.FC<{
  showMenu: boolean;
  menuType: "useLeftRightForMenu" | "differentMenu";
  interactionsLeft?: InteractionProps[];
  interactionsRight?: InteractionProps[];
  interactionsMenu?: InteractionMenuItem[];
  testID: TestID;
  isDisabled: boolean;
  children?: React.ReactNode;
}> = ({
  showMenu,
  menuType,
  interactionsMenu,
  interactionsLeft,
  interactionsRight,
  testID,
  isDisabled,
  children,
}) => {
  let menuItems: InteractionMenuItem[] = [];
  if (!showMenu) return null;

  if (menuType === "useLeftRightForMenu") {
    const interactionsLeftRight = interactionsLeft?.concat(
      interactionsRight ? interactionsRight : []
    );
    menuItems = interactionsLeftRight
      ? interactionsLeftRight.map((interaction) => {
          testID = (testID + interaction.key) as TestID;
          switch (interaction.type) {
            case COREButton:
            case CORECSVButton: {
              const coreCsvButton =
                interaction.props as unknown as CORECSVButtonProps & {
                  to?: string;
                  href?: string;
                  disabled?: boolean;
                };
              return {
                component: (
                  <COREMenuItem
                    testID={testID}
                    key={testID}
                    icon={coreCsvButton.icon}
                    to={coreCsvButton.to}
                    href={coreCsvButton.href}
                    disabled={coreCsvButton.disabled}
                    onClick={
                      (
                        interaction as React.ReactElement<{
                          onClick?: () => void;
                        }>
                      ).props.onClick
                    }
                  >
                    {coreCsvButton.children}
                  </COREMenuItem>
                ),
                closeWhenClick: true,
              } as InteractionMenuItem;
            }
            case CORESelect:
              return {
                component: (
                  <COREMenuItem testID={testID} key={testID}>
                    <div>
                      <CORESelect
                        {...(interaction.props as unknown as CORESelectProps)}
                        placement={"bottomRight"}
                      />
                    </div>
                  </COREMenuItem>
                ),
                closeWhenClick: false,
              } as InteractionMenuItem;
            default:
              return {
                component: (
                  <COREMenuItem testID={testID} key={testID}>
                    {interaction}
                  </COREMenuItem>
                ),
                closeWhenClick: false,
              } as InteractionMenuItem;
          }
        })
      : [];
  } else {
    menuItems = interactionsMenu ? interactionsMenu : [];
  }

  if (menuItems.length === 0) return null;

  return (
    <COREDropdownMenu
      interactionsMenu={menuItems}
      isDisabled={isDisabled}
      testID={(testID + "-dropdown-menu") as TestID}
      className={"accordion-dropdown-menu"}
    >
      {children}
    </COREDropdownMenu>
  );
};

const Interactions = ({
  interactions,
  space,
  menu,
  interactionsClassName,
  showMenu,
  isDisabled,
}: {
  space: number;
  interactions?: InteractionProps[];
  menu?: React.ReactElement<typeof InteractionMenuButton>;
  interactionsClassName?: string;
  showMenu: boolean;
  isDisabled: boolean;
}) => (
  <div
    className={"interactions"}
    onClick={(event) => {
      event.stopPropagation();
    }}
  >
    <Space size={space}>
      {!showMenu &&
        interactions &&
        interactions
          .filter((l) => l)
          .map((l, i) => (
            <div
              className={interactionsClassName}
              key={`warpper-${i}-${l?.key}`}
            >
              {React.cloneElement(l as React.ReactElement, {
                disabled: isDisabled,
                ...l.props,
              })}
            </div>
          ))}
      {menu}
    </Space>
  </div>
);

const HeaderWithInteractions: React.FC<HeaderWithInteractionsProps> = ({
  stopPropagationWhenNotCollapsed,
  headerSummary,
  collapsible,
  expandIcon,
  isActive,
  testID,
  header,
  showMenu,
  interactionsLeft,
  interactionSpace,
  isDisabled,
  showExpandIconWhenDisabled,
  fullWidthHeader,
}) => {
  return (
    <Row
      onClick={stopPropagationWhenNotCollapsed}
      gutter={[spacing.sm, spacing.sm]}
      align="middle"
      wrap={false}
    >
      <Col
        {...(fullWidthHeader
          ? { flex: "auto" }
          : headerSummary
          ? { flex: "190px" }
          : { span: 24 })}
      >
        <Row align="middle">
          {(collapsible === undefined || showExpandIconWhenDisabled) &&
            expandIcon && <Col>{expandIcon({ isActive: isActive })}</Col>}
          <Col flex="auto">
            <div className={"collapse-header-block"}>
              <COREHeading marginBottom={false} testID={testID} level={2}>
                {header}
              </COREHeading>
            </div>
          </Col>
        </Row>
      </Col>
      {headerSummary && !isActive && <Col span={12}>{headerSummary}</Col>}
      {!showMenu && interactionsLeft && interactionsLeft.length > 0 && (
        <Col>
          <div className={"interactions-left"}>
            <Interactions
              space={interactionSpace}
              interactions={interactionsLeft}
              showMenu={showMenu}
              isDisabled={isDisabled}
            />
          </div>
        </Col>
      )}
    </Row>
  );
};

export const COREAccordionItem: React.FC<COREAccordionItemProps> = ({
  testID,
  isOpen = true,
  interactionsLeft = [],
  interactionsRight = [],
  interactionsMenu = [],
  progress,
  collapsible,
  interactionSpace = 8,
  bannerImageUrl,
  bannerImageHeight = 220,
  noScroll = false,
  allowOverflow,
  children,
  className,
  menuType = "useLeftRightForMenu",
  onShowMenu,
  expandIconSize,
  maxHeight,
  header,
  headerSummary,
  expandIcon,
  type = "container",
  roundCorner = true,
  showContentLockedTooltip,
  upgradeType,
  showExpandIconWhenDisabled,
  fullWidthHeader,
  flushHeader,
  ...props
}) => {
  let isActive = isOpen;

  if (!isActive) {
    interactionsLeft = [];
    interactionsRight = [];
    interactionsMenu = [];
  }

  const showArrow = collapsible !== "header";
  const isDisabled = collapsible === "disabled";
  const panelClass = classNames(className, "core-container-panel", {
    "panel-style": type === "panel",
    "round-corner-panel": roundCorner,
    "flush-header": flushHeader,
  });
  const [panelSize, setPanelSize] = useState<SetSizeObject>(defaultResizeValue);
  const [contentWidth, setContentWidth] = useState<number>(0);
  const leftRef = useRef<HTMLDivElement>(null);
  const rightRef = useRef<HTMLDivElement>(null);

  const panelSizeWidth =
    typeof panelSize.width === "number" && Number.isFinite(panelSize.width)
      ? panelSize.width
      : 0;

  const WIDTH_BUFFER = 32;

  useLayoutEffect(() => {
    const measure = () => {
      const width =
        (leftRef.current?.offsetWidth || 0) +
        (rightRef.current?.offsetWidth || 0);
      setContentWidth(width);
    };

    measure();

    const observer = new ResizeObserver(() => {
      // Use requestAnimationFrame to avoid "ResizeObserver loop limit exceeded"
      requestAnimationFrame(() => measure());
    });

    if (leftRef.current) observer.observe(leftRef.current);
    if (rightRef.current) observer.observe(rightRef.current);

    return () => {
      observer.disconnect();
    };
  }, [
    interactionsLeft,
    interactionsRight,
    interactionsMenu,
    header,
    headerSummary,
    expandIcon,
    collapsible,
    showExpandIconWhenDisabled,
    interactionSpace,
  ]);

  const showMenu =
    panelSizeWidth > 0 && contentWidth > 0
      ? panelSizeWidth - contentWidth < WIDTH_BUFFER
      : false;

  useEffect(() => {
    onShowMenu && onShowMenu(showMenu);
  }, [onShowMenu, showMenu]);

  const stopPropagationWhenNotCollapsed = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    if (collapsible === "header") {
      e.stopPropagation();
    }
  };

  const headerPanel = (showMenu: boolean) =>
    collapsible === "header" ? (
      <Row onClick={stopPropagationWhenNotCollapsed}>
        <Col span={24}>
          <HeaderWithInteractions
            stopPropagationWhenNotCollapsed={stopPropagationWhenNotCollapsed}
            headerSummary={headerSummary}
            collapsible={collapsible}
            expandIcon={expandIcon}
            isActive={isActive}
            testID={testID}
            header={header}
            showMenu={showMenu}
            interactionsLeft={interactionsLeft}
            interactionSpace={interactionSpace}
            isDisabled={isDisabled}
            showExpandIconWhenDisabled={showExpandIconWhenDisabled}
            fullWidthHeader={fullWidthHeader}
          />
        </Col>
      </Row>
    ) : (
      <HeaderWithInteractions
        stopPropagationWhenNotCollapsed={stopPropagationWhenNotCollapsed}
        headerSummary={headerSummary}
        collapsible={collapsible}
        expandIcon={expandIcon}
        isActive={isActive}
        testID={testID}
        header={header}
        showMenu={showMenu}
        interactionsLeft={interactionsLeft}
        interactionSpace={interactionSpace}
        isDisabled={isDisabled}
        showExpandIconWhenDisabled={showExpandIconWhenDisabled}
        fullWidthHeader={fullWidthHeader}
      />
    );

  const extra = (showMenu: boolean) => (
    <Space className={"space-interaction"} align="center">
      <Interactions
        isDisabled={isDisabled}
        space={interactionSpace}
        interactionsClassName={"interactions-right"}
        interactions={interactionsRight}
        menu={
          <InteractionMenuButton
            isDisabled={isDisabled}
            showMenu={showMenu}
            interactionsRight={interactionsRight}
            interactionsLeft={interactionsLeft}
            interactionsMenu={interactionsMenu}
            menuType={menuType}
            testID={testID}
          >
            <COREButton
              type={"default"}
              size={"md"}
              icon={
                <COREIcon icon={icon({ name: "bars", style: "regular" })} />
              }
              testID={`${testID}-bars-button`}
            ></COREButton>
          </InteractionMenuButton>
        }
        showMenu={showMenu}
      />
    </Space>
  );

  const headerWithExtra = (
    <Row
      className={"header-row"}
      gutter={[0, spacing.sm]}
      justify="space-between"
      wrap={false}
      onClick={stopPropagationWhenNotCollapsed}
    >
      <Col {...(fullWidthHeader ? { flex: "auto" } : {})}>
        {headerPanel(showMenu)}
      </Col>
      <Col flex="none">{extra(showMenu)}</Col>
    </Row>
  );

  const headerMeasure = (
    <OnResize
      onResize={(current: React.SetStateAction<SetSizeObject>) => {
        const newSize =
          typeof current === "function" ? current(panelSize) : current;
        setPanelSize((prev) => {
          if (prev.width === newSize.width && prev.height === newSize.height) {
            return prev;
          }
          return newSize;
        });
      }}
    >
      <Row
        className={"header-row"}
        gutter={[0, spacing.sm]}
        justify="space-between"
        wrap={false}
      >
        <Col {...(fullWidthHeader ? { flex: "auto" } : {})}>
          <div ref={leftRef}>{headerPanel(false)}</div>
        </Col>
        <Col flex="none">
          <div ref={rightRef}>{extra(false)}</div>
        </Col>
      </Row>
    </OnResize>
  );

  const cover = bannerImageUrl ? (
    <div
      className={classNames("image-cover", "custom-image-cover")}
      style={{
        height: bannerImageHeight,
        backgroundImage: `url(${bannerImageUrl})`,
      }}
    />
  ) : null;

  const progressBar = progress ? (
    <div className={"progress-bar"}>{progress}</div>
  ) : null;

  const bodyClassNames = classNames(
    "panel-body",
    { "with-progress-bar": progress },
    { "no-scroll": noScroll },
    { "allow-overflow": allowOverflow }
  );

  const accordionItem = (
    <Collapse.Panel
      {...props}
      className={panelClass}
      data-testid={testID}
      header={
        <div className={"accordion-header-wrapper"}>
          <div>{headerWithExtra}</div>
          <div
            className={"accordion-header-measure"}
            aria-hidden={true}
            data-measure-only={true}
          >
            {headerMeasure}
          </div>
        </div>
      }
      showArrow={showArrow}
      collapsible={collapsible}
    >
      {progressBar}
      {cover}
      <div className={"panel-body-padding"}>
        <div
          className={bodyClassNames}
          style={maxHeight ? { maxHeight: maxHeight } : undefined}
        >
          {children}
        </div>
      </div>
    </Collapse.Panel>
  );

  if (isDisabled && showContentLockedTooltip && upgradeType) {
    return (
      <TestIDWrapper testID={testID} className={"core-container-panel-block"}>
        <ContentLockedTooltip
          testID={`${testID}-lock`}
          upgradeType={upgradeType}
          showHint={false}
          displayTooltip={true}
          noFitContent={true}
        >
          {accordionItem}
        </ContentLockedTooltip>
      </TestIDWrapper>
    );
  }

  return (
    <TestIDWrapper testID={testID} className={"core-container-panel-block"}>
      {accordionItem}
    </TestIDWrapper>
  );
};
